import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import Header from "../../components/header";
import AIChatSidebar from "../../components/ai/AIChatSidebar";
import AIChatWindow from "../../components/ai/AIChatWindow";
import {
  createConversation,
  getConversations,
  getConversation,
  deleteConversation,
  sendMessage,
} from "../../functions/ai";
import "./ChatBot.css";

export default function ChatBot() {
  const { user } = useSelector((state) => ({ ...state }));

  const [conversations, setConversations] = useState([]);
  const [activeConversation, setActiveConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [error, setError] = useState(null);

  // Load conversations
  useEffect(() => {
    loadConversations();
  }, []);

  const loadConversations = async () => {
    try {
      const data = await getConversations("chatbot", user.token);
      setConversations(data);
    } catch (error) {
      console.error("Error loading conversations:", error);
    }
  };

  const handleNewConversation = async () => {
    try {
      const conversation = await createConversation(
        "chatbot",
        "Nova conversa",
        null,
        user.token
      );
      setConversations([conversation, ...conversations]);
      setActiveConversation(conversation);
      setMessages([]);
    } catch (error) {
      console.error("Error creating conversation:", error);
    }
  };

  const handleSelectConversation = async (conversation) => {
    try {
      setActiveConversation(conversation);
      const data = await getConversation(conversation._id, user.token);
      setMessages(data.messages);
    } catch (error) {
      console.error("Error loading conversation:", error);
    }
  };

  const handleDeleteConversation = async (conversationId) => {
    try {
      await deleteConversation(conversationId, user.token);
      setConversations(conversations.filter((c) => c._id !== conversationId));
      if (activeConversation?._id === conversationId) {
        setActiveConversation(null);
        setMessages([]);
      }
    } catch (error) {
      console.error("Error deleting conversation:", error);
    }
  };

  const handleSendMessage = async (content) => {
    let currentConversation = activeConversation;

    // Create conversation if none exists
    if (!currentConversation) {
      try {
        currentConversation = await createConversation(
          "chatbot",
          content.substring(0, 50),
          null,
          user.token
        );
        setConversations([currentConversation, ...conversations]);
        setActiveConversation(currentConversation);
      } catch (error) {
        console.error("Error creating conversation:", error);
        return;
      }
    }

    // Add user message optimistically
    const tempUserMessage = {
      _id: Date.now().toString(),
      role: "user",
      content,
      createdAt: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, tempUserMessage]);
    setIsLoading(true);

    try {
      const response = await sendMessage(
        currentConversation._id,
        content,
        false, // generateImage
        user.token
      );

      // Update messages with actual response
      setMessages((prev) => [
        ...prev.filter((m) => m._id !== tempUserMessage._id),
        response.userMessage,
        response.assistantMessage,
      ]);

      // Update conversation title if it was the first message
      if (messages.length === 0) {
        setConversations((prev) =>
          prev.map((c) =>
            c._id === currentConversation._id
              ? { ...c, title: content.substring(0, 50) }
              : c
          )
        );
      }
    } catch (error) {
      console.error("Error sending message:", error);
      // Remove optimistic message on error
      setMessages((prev) => prev.filter((m) => m._id !== tempUserMessage._id));
      setError("Erro ao enviar mensagem. Por favor, tente novamente.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleGenerateImage = async (prompt) => {
    let currentConversation = activeConversation;

    // Create conversation if none exists
    if (!currentConversation) {
      try {
        currentConversation = await createConversation(
          "chatbot",
          `Imagem: ${prompt.substring(0, 40)}`,
          null,
          user.token
        );
        setConversations([currentConversation, ...conversations]);
        setActiveConversation(currentConversation);
      } catch (error) {
        console.error("Error creating conversation:", error);
        setError("Erro ao criar conversa.");
        return;
      }
    }

    // Add user message for the image request
    const tempUserMessage = {
      _id: Date.now().toString(),
      role: "user",
      content: `Gerar imagem: ${prompt}`,
      createdAt: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, tempUserMessage]);
    setIsLoading(true);
    setError(null);

    try {
      const response = await sendMessage(
        currentConversation._id,
        `Gere uma imagem: ${prompt}`,
        true, // generateImage = true
        user.token
      );

      // Update messages with actual response
      setMessages((prev) => [
        ...prev.filter((m) => m._id !== tempUserMessage._id),
        response.userMessage,
        response.assistantMessage,
      ]);

      // Update conversation title
      if (messages.length === 0) {
        setConversations((prev) =>
          prev.map((c) =>
            c._id === currentConversation._id
              ? { ...c, title: `Imagem: ${prompt.substring(0, 40)}` }
              : c
          )
        );
      }
    } catch (error) {
      console.error("Error generating image:", error);
      setMessages((prev) => prev.filter((m) => m._id !== tempUserMessage._id));
      setError("Erro ao gerar imagem. Por favor, tente novamente.");
    } finally {
      setIsLoading(false);
    }
  };

  const welcomeContent = {
    title: "Olá, sou o Socializa AI",
    subtitle: "Como posso ajudá-lo hoje?",
    suggestions: [
      {
        icon: "✍️",
        text: "Ajude-me a escrever um e-mail profissional",
        prompt: "Ajude-me a escrever um e-mail profissional",
      },
      {
        icon: "💡",
        text: "Dê-me ideias criativas para um projeto",
        prompt: "Dê-me ideias criativas para um projeto",
      },
      {
        icon: "💻",
        text: "Explique um conceito de programação",
        prompt: "Explique-me como funciona a programação assíncrona em JavaScript",
      },
      {
        icon: "📝",
        text: "Resuma um texto ou documento",
        prompt: "Preciso que você resuma um texto para mim",
      },
    ],
  };

  return (
    <div className="chatbot_page">
      <Header page="ai" />
      <div className="chatbot_container">
        <AIChatSidebar
          conversations={conversations}
          activeConversation={activeConversation}
          onSelectConversation={handleSelectConversation}
          onNewConversation={handleNewConversation}
          onDeleteConversation={handleDeleteConversation}
          type="chatbot"
          isCollapsed={isSidebarCollapsed}
          onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
        />

        <AIChatWindow
          messages={messages}
          onSendMessage={handleSendMessage}
          isLoading={isLoading}
          type="chatbot"
          conversation={activeConversation}
          showWelcome={true}
          welcomeContent={welcomeContent}
          inputPlaceholder="Envie uma mensagem para o Socializa AI..."
          user={user}
          showImageButton={true}
          onGenerateImage={handleGenerateImage}
          error={error}
          onClearError={() => setError(null)}
        />
      </div>
    </div>
  );
}
